import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { findSeriesById, jeeUltimateCombo, neetUltimatePremium, jeePremiumSeries, neetPremiumSeries } from '../../data/testSeriesData';
import { useAuth } from '../../context/AuthContext';
import { initiatePurchase } from '../../utils/paymentHandler';
import Toast from '../../components/Toast';
import { checkIfPurchased, getUserPurchases } from '../../utils/purchaseHelper';
import { 
    FaShieldHalved, FaRegClock, FaCircleCheck, FaChevronRight,
    FaGraduationCap, FaUserTie, FaLock, FaLayerGroup, FaFire, FaGift, FaStar
} from 'react-icons/fa6';
import FAQSection from '../../components/FAQSection';
import SocialSidebar from '../../components/SocialSideBar';
import './TestSeriesDetailPage.css';

const TestSeriesDetailPage = () => {
    const { productId } = useParams();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [series, setSeries] = useState(null);
    const [isPurchased, setIsPurchased] = useState(false);
    const [isPurchasedViaCombo, setIsPurchasedViaCombo] = useState(false);
    const [purchasedComboId, setPurchasedComboId] = useState(null);
    const [checkingPurchase, setCheckingPurchase] = useState(true);
    const [toast, setToast] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isPartOfCombo, setIsPartOfCombo] = useState(false);
    const [includedSeries, setIncludedSeries] = useState([]);
    const isCombo = !!(series?.Contains_ids?.length > 0);

    useEffect(() => {
        const found = findSeriesById(productId);
        if (found) {
            setSeries(found);
            // Check if this series is part of any combo
            const allCombos = [...jeeUltimateCombo, ...neetUltimatePremium];
            const partOfCombo = allCombos.some(combo => combo.Contains_ids?.includes(found.id));
            setIsPartOfCombo(partOfCombo);
            // Resolve included series for combo pages
            if (found.Contains_ids?.length > 0) {
                const allSeries = [...jeePremiumSeries, ...neetPremiumSeries];
                setIncludedSeries(found.Contains_ids.map(id => allSeries.find(s => s.id === id)).filter(Boolean));
            }
        }
    }, [productId]);

    // Check if user has already purchased this series
    useEffect(() => {
        const checkPurchase = async () => {
            if (currentUser && series?.id) {
                setCheckingPurchase(true);
                const purchased = await checkIfPurchased(currentUser.uid, series.id);
                setIsPurchased(purchased);

                // Check if purchased via combo
                const purchases = await getUserPurchases(currentUser.uid);
                const purchasedComboIds = purchases
                    .map(p => p.courseId)
                    .filter(id => id && findSeriesById(id)?.Contains_ids);
                const purchasedCombos = purchasedComboIds.map(id => findSeriesById(id));
                const comboIdx = purchasedCombos.findIndex(combo => combo.Contains_ids?.includes(series.id));
                const isViaCombo = comboIdx !== -1;
                setIsPurchasedViaCombo(isViaCombo);
                setPurchasedComboId(isViaCombo ? purchasedComboIds[comboIdx] : null);

                setCheckingPurchase(false);
            } else {
                setCheckingPurchase(false);
            }
        };
        checkPurchase();
    }, [currentUser, series]);

    if (!series) return <div className="loading-screen">Preparing curriculum...</div>;

    const discount = Math.round((1 - parseInt(series.price.replace(/[^\d]/g, '')) / parseInt(series.oldPrice.replace(/[^\d]/g, ''))) * 100);

    const handleBuy = async () => {
        setIsProcessing(true);
        setToast({ message: 'Processing payment, please wait...', type: 'info' });
        await initiatePurchase(
            series,
            currentUser,
            navigate,
            (msg, type = 'info') => setToast({ message: msg, type })
        );
        setIsProcessing(false);
    };

    return (
        <div className={`ts-detail-root ${isCombo ? 'ts-combo-root' : ''}`}>
            <SocialSidebar />
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
            <div className="ts-page-wrapper">
                {/* HERO SECTION */}
                <header className={`ts-hero-v5 ${isCombo ? 'ts-hero-combo' : ''}`}>
                    <div className="ts-container">
                        <div className="ts-hero-inner">
                            {isCombo ? (
                                <div className="ts-combo-hero-badges">
                                    <span className="ts-badge-combo-hot"><FaFire /> BEST VALUE PACK</span>
                                    <span className="ts-badge-combo-save">Save {discount}% OFF</span>
                                </div>
                            ) : (
                                <div className="ts-badge-alumni">
                                    <FaGraduationCap /> Created by IIT, NIT & Top Medical Alumni
                                </div>
                            )}
                            <h1 className="ts-main-title">{series.title}</h1>
                            <p className="ts-main-subtitle">
                                {series.subtitle || "Master the competitive landscape with curricula engineered by India's top academic rankers."}
                            </p>

                            {isCombo && (
                                <div className="ts-combo-includes-bar">
                                    {includedSeries.map((s, i) => (
                                        <span key={s.id} className="ts-combo-inc-chip">
                                            <FaCircleCheck /> {s.title.replace('JEE ', '').replace('NEET ', '')}
                                        </span>
                                    ))}
                                </div>
                            )}
                            
                            <div className="ts-hero-stats">
                                <div className="hero-stat-box"><strong>{series.NumberOfTests}+</strong> <span>Tests</span></div>
                                <div className="hero-stat-box"><strong>15-Day</strong> <span>Refund</span></div>
                                <div className="hero-stat-box">
                                    <strong>{series.ValidityYears || 1} Year{(series.ValidityYears || 1) > 1 ? 's' : ''}</strong>
                                    <span>Validity</span>
                                </div>
                                {isCombo && <div className="hero-stat-box hero-stat-box-gold"><strong>3-in-1</strong> <span>Series</span></div>}
                            </div>
                        </div>
                    </div>
                </header>

                <main className="ts-container ts-content-grid">
                    <div className="ts-main-column">

                        {/* COMBO — What's Included Section */}
                        {isCombo && includedSeries.length > 0 && (
                            <section className="ts-info-block ts-combo-included-section">
                                <h2 className="ts-block-title"><FaLayerGroup style={{marginRight:'10px',color:'#3b82f6'}}/>What's Included</h2>
                                <div className="ts-combo-cards-grid">
                                    {includedSeries.map((s, i) => {
                                        const icons = ['📘', '📗', '📙'];
                                        const indivPrice = parseInt(s.price.replace(/[^\d]/g, ''));
                                        return (
                                            <div key={s.id} className="ts-combo-card">
                                                <div className="ts-combo-card-header">
                                                    <span className="ts-combo-card-icon">{icons[i]}</span>
                                                    <span className="ts-combo-card-num">Series {i + 1}</span>
                                                </div>
                                                <h3 className="ts-combo-card-title">{s.title}</h3>
                                                <p className="ts-combo-card-sub">{s.subtitle?.split('.')[0]}.</p>
                                                <ul className="ts-combo-card-stats">
                                                    <li><FaCircleCheck />{s.NumberOfTests} Tests</li>
                                                    <li><FaRegClock />{s.TestTime} min each</li>
                                                </ul>
                                                <div className="ts-combo-card-price">
                                                    <span className="ts-combo-card-orig">{s.oldPrice}</span>
                                                    <span className="ts-combo-card-curr">₹{indivPrice}</span>
                                                    <span className="ts-combo-card-tag">individually</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Value Banner */}
                                <div className="ts-combo-value-banner">
                                    <div className="ts-cvb-left">
                                        <FaGift className="ts-cvb-icon" />
                                        <div>
                                            <div className="ts-cvb-label">Individual Total</div>
                                            <div className="ts-cvb-price-strike">₹{includedSeries.reduce((a,s)=>a+parseInt(s.price.replace(/[^\d]/g,'')),0)}</div>
                                        </div>
                                    </div>
                                    <div className="ts-cvb-arrow">→</div>
                                    <div className="ts-cvb-right">
                                        <div className="ts-cvb-label-combo">Combo Price</div>
                                        <div className="ts-cvb-combo-price">₹{parseInt(series.price.replace(/[^\d]/g,''))}</div>
                                    </div>
                                    <div className="ts-cvb-saving">
                                        <FaStar />Save ₹{includedSeries.reduce((a,s)=>a+parseInt(s.price.replace(/[^\d]/g,'')),0) - parseInt(series.price.replace(/[^\d]/g,''))} extra
                                    </div>
                                </div>
                            </section>
                        )}

                        <section className="ts-info-block">
                            <h2 className="ts-block-title">Test Benefits</h2>
                            <div className="ts-outcomes-list">
                                {series.benefits.map((benefit, index) => (
                                    <div key={index} className="ts-outcome-card">
                                        <FaCircleCheck className="ts-check-icon" />
                                        <span>{benefit.includes('30-day') ? benefit.replace('30-day', '15-day') : benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="ts-info-block alumni-focus">
                            <div className="ts-alumni-banner">
                                <FaUserTie className="ts-alumni-avatar" />
                                <div>
                                    <h3>The Alumni Advantage</h3>
                                    <p>Our assessment modules are strictly curated by IIT/NIT and Medical alumni to ensure perfect alignment with current NTA difficulty standards.</p>
                                </div>
                            </div>
                        </section>
                    </div>

                    <aside className="ts-sticky-sidebar">
                        <div className="ts-checkout-module">
                            <div className="ts-checkout-pricing">
                                <div className="ts-price-primary">{series.price.startsWith('₹') ? series.price : `₹${series.price}`}</div>
                                <div className="ts-price-secondary">{series.oldPrice}</div>
                                <div className="ts-save-pill">Save {discount}%</div>
                                {isCombo && (
                                    <div className="ts-combo-sidebar-tag"><FaLayerGroup /> 3-in-1 Combo Pack • {series.NumberOfTests}+ Tests</div>
                                )}
                            </div>

                            <ul className="ts-checkout-list">
                                <li><FaUserTie /> {series.NumberOfTests} Tests</li>
                                <li><FaShieldHalved /> 15-Day Refund Policy</li>
                            
                                <li>
                                    <FaCircleCheck /> 
                                    Validity: {isPurchasedViaCombo ? (
                                        <>
                                            <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: '8px' }}>
                                                {series.ValidityYears ? `${series.ValidityYears} Year${series.ValidityYears > 1 ? 's' : ''}` : '1 Year'}
                                            </span>
                                            <strong>2 Years</strong> <span style={{ color: '#10b981', fontWeight: '600' }}>(booked via combo)</span>
                                        </>
                                    ) : (
                                        <>
                                            {series.ValidityYears ? `${series.ValidityYears} Year${series.ValidityYears > 1 ? 's' : ''}` : '1 Year'}
                                            {isPartOfCombo && series.ValidityYears === 1 && (
                                                <span className="combo-badge-sidebar">
                                                    2Y via Combo 🎁
                                                </span>
                                            )}
                                        </>
                                    )}
                                </li>
                            </ul>

                            {(isPurchased || isPurchasedViaCombo) ? (
                                <>
                                    <button 
                                        className="ts-btn-enroll-full ts-btn-purchased"
                                        onClick={() => navigate('/profile')}
                                        disabled
                                    >
                                        <FaCircleCheck /> {isPurchasedViaCombo ? 'Purchased (Combo)' : 'Already Purchased'}
                                    </button>
                                    <button 
                                        className="ts-btn-secondary"
                                        onClick={() => {
                                            if (isPurchasedViaCombo && purchasedComboId) {
                                                const comboSeries = findSeriesById(purchasedComboId);
                                                navigate(`/purchased-tests/${series.id}`, {
                                                    state: {
                                                        comboInfo: {
                                                            comboId: purchasedComboId,
                                                            comboTitle: comboSeries?.title || 'Combo Pack',
                                                        }
                                                    }
                                                });
                                            } else {
                                                navigate(`/purchased-tests/${series.id}`);
                                            }
                                        }}
                                    >
                                        View Tests <FaChevronRight />
                                    </button>
                                </>
                            ) : (
                                <button 
                                    className="ts-btn-enroll-full"
                                    onClick={handleBuy}
                                    disabled={checkingPurchase || isProcessing}
                                >
                                    {checkingPurchase ? 'Loading...' : isProcessing ? 'Processing...' : 'Buy Now'} <FaChevronRight />
                                </button>
                            )}
                            
                            <div className="ts-trust-container">
                                <div className="ts-trust-row">
                                    <FaLock className="trust-icon-main" />
                                    <div className="trust-text">
                                        <strong>Secure Payment</strong>
                                        <span>SSL Encrypted</span>
                                    </div>
                                </div>
                                
                                <div className="ts-exam-badges">
                                    <div className="badge-item">
                                        <span className="badge-dot"></span>
                                        NTA Pattern
                                    </div>
                                    <div className="badge-item">
                                        <span className="badge-dot"></span>
                                        JEE/NEET Level
                                    </div>
                                </div>

                                <p className="ts-social-proof">
                                    <strong>50,000+</strong> Students
                                </p>
                            </div>

                        </div>
                    </aside>
                </main>
                <div style={{ margin: '2.5rem 0' }}>
                  {/* FAQ Section: Test Series Detail Page */}
                <FAQSection
                    title={`FAQs about ${series.title}`}
                    faqs={[
                        {
                            question: "What is the validity of this test series?",
                            answer: isPurchasedViaCombo
                                ? "Your access is valid for 2 years from the date of purchase (extended validity via combo pack)."
                                : series.ValidityYears
                                ? `Your access is valid for ${series.ValidityYears} year${series.ValidityYears > 1 ? 's' : ''} from the date of purchase.`
                                : "Your access is valid for 1 year from the date of purchase."
                        },
                      {
                        question: "Can I attempt the tests multiple times?",
                        answer: "Each test can be attempted once to simulate the real exam experience. However, you can review solutions and your performance anytime."
                      },
                      {
                        question: "Are solutions provided for every question?",
                        answer: "Yes, detailed step-by-step solutions are available for every question immediately after you finish the test."
                      },
                      {
                        question: "Is there a refund if I am not satisfied?",
                        answer: "Yes, we offer a 15-day, no-questions-asked refund policy for all premium test series."
                      },
                      {
                        question: "How do I access my purchased tests?",
                        answer: "After purchase, your tests are available in your profile dashboard under 'Purchased Tests'."
                      },
                      {
                        question: "Who prepares the questions?",
                        answer: "All questions are curated by IIT/NIT/Medical alumni and subject experts to match the latest exam standards."
                      },
                      {
                        question: "Can I get support if I face any issues?",
                        answer: "Yes, our support team is available to help you with any technical or academic queries."
                      }
                    ]}
                  />
                </div>
            </div>
        </div>
    );
};

export default TestSeriesDetailPage;